import { computed } from "vue";
import { defineStore } from "pinia";

import { useWorldStore } from "./world";
import { useTourStore } from "./tour";
import { mergeWorldTours, transformContinents } from "../utils/dataTransform";

export const useDataStore = defineStore("data", () => {
    const worldStore = useWorldStore();
    const tourStore = useTourStore();

    const loaded = computed(
        () => worldStore.loaded && tourStore.tours.length > 0,
    );

    const _merged = computed(() => {
        if (!loaded.value) return null;
        return mergeWorldTours(worldStore.world, tourStore.tours);
    });

    const _transformed = computed(() =>
        _merged.value ? transformContinents(_merged.value) : null,
    );

    const pinnedCities = computed(() => _transformed.value?.pinnedCities ?? []);
    const continentOrder = computed(
        () => _transformed.value?.continentOrder ?? {},
    );
    const searchableCities = computed(
        () => _transformed.value?.searchableCities ?? [],
    );
    const continentLabels = computed(
        () => _transformed.value?.continentLabels ?? {},
    );

    const countryCodeMap = computed(() => {
        const map = new Map();
        for (const continent of worldStore.world) {
            for (const country of continent.countries ?? []) {
                map.set(country.code, {
                    name: country.name,
                    locked: country.locked ?? false,
                    continent: continent.code,
                    description: country.description ?? null,
                });
            }
        }
        return map;
    });

    const countryByName = computed(() => {
        const map = new Map();
        for (const continent of worldStore.world) {
            for (const country of continent.countries ?? []) {
                map.set(country.name, country);
            }
        }
        return map;
    });

    const provincesData = computed(() => {
        const result = [];
        for (const continent of worldStore.world) {
            for (const country of continent.countries ?? []) {
                for (const province of country.provinces ?? []) {
                    result.push({
                        name: province.name,
                        code: province.code,
                        countryCode: country.code,
                        countryName: country.name,
                        continent: continent.code,
                        lat: parseFloat(province.latitude),
                        lng: parseFloat(province.longitude),
                        description: province.description ?? null,
                        locked: country.locked ?? false,
                        flight_url: province.flight_url ?? null,
                        guide_url: province.guide_url ?? null,
                        guide_image: province.guide_image ?? null,
                    });
                }
            }
        }
        return result;
    });

    const provinceByName = computed(() => {
        const map = new Map();
        for (const p of provincesData.value) {
            map.set(p.name, p);
        }
        return map;
    });

    return {
        loaded,
        pinnedCities,
        continentOrder,
        searchableCities,
        continentLabels,
        countryCodeMap,
        countryByName,
        provincesData,
        provinceByName,
    };
});
